import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import type { NoteOut, NoteTargetKind } from "../api/notes";
import {
  useNotesByTarget,
  useCreateNote,
  usePatchNote,
  useDeleteNote,
} from "./hooks";
import { NoteRow } from "./NoteRow";
import { NoteComposer } from "./NoteComposer";

interface NotesSlideOverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: { kind: NoteTargetKind; id: string; title?: string | null };
  currentUserId: string | null;
}

export function NotesSlideOver({
  open,
  onOpenChange,
  target,
  currentUserId,
}: NotesSlideOverProps) {
  const { data, isLoading } = useNotesByTarget({
    targetKind: target.kind,
    targetId: target.id,
  });
  const createMut = useCreateNote();
  const patchMut = usePatchNote();
  const deleteMut = useDeleteNote();
  const [editing, setEditing] = useState<NoteOut | null>(null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[360px] sm:w-[420px]">
        <SheetHeader>
          <SheetTitle className="text-base">
            Notes · {target.kind}{target.title ? `: ${target.title}` : ""}
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="my-4 max-h-[calc(100vh-260px)]">
          <div className="flex flex-col gap-2 pe-2">
            {isLoading ? (
              <>
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </>
            ) : data?.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No notes yet. Add one below.
              </p>
            ) : (
              data?.items.map((n) =>
                editing?.id === n.id ? (
                  <NoteComposer
                    key={n.id}
                    initialBody={n.body}
                    isSubmitting={patchMut.isPending}
                    onCancel={() => setEditing(null)}
                    onSubmit={(body) =>
                      patchMut.mutate(
                        { id: n.id, patch: { body } },
                        { onSuccess: () => setEditing(null) }
                      )
                    }
                  />
                ) : (
                  <NoteRow
                    key={n.id}
                    note={n}
                    currentUserId={currentUserId}
                    onEdit={(note) => setEditing(note)}
                    onArchiveToggle={(note) =>
                      patchMut.mutate({
                        id: note.id,
                        patch: { archived: note.archived_at === null },
                      })
                    }
                    onDelete={(note) => deleteMut.mutate(note.id)}
                  />
                )
              )
            )}
          </div>
        </ScrollArea>
        <div className="border-t border-border pt-3">
          <NoteComposer
            isSubmitting={createMut.isPending}
            onCancel={() => onOpenChange(false)}
            onSubmit={(body) =>
              createMut.mutate({
                target_kind: target.kind,
                target_id: target.id,
                body,
              })
            }
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
